/**
 * Romania's civil time and its legal holidays: what "08:00" and "a working day"
 * mean on the shop's calendar once it is saved ("Zile și program de lucru"),
 * and what the timesheet (Pontaj lunar) enters and shows.
 *
 * Time is Europe/Bucharest: EET (UTC+2), and EEST (UTC+3) from the last Sunday
 * of March to the last Sunday of October, the clocks changing at 01:00 UTC as
 * everywhere in the European Union. It is computed by that rule rather than
 * through the runtime's time-zone data, so the API, the browser and the tests
 * agree on every instant.
 *
 * A time of day is "minutes from 00:00" of a calendar date, the way the
 * workers' programs and the shop's program store it. The two changeover
 * Sundays are 23 and 25 hours long: a time that does not exist (03:00–04:00 in
 * March) reads as the hour after it, one that happens twice (03:00–04:00 in
 * October) as the second of the two.
 */
export const ROMANIA_TIME_ZONE = 'Europe/Bucharest';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WINTER_OFFSET_MS = 2 * HOUR_MS;
const SUMMER_OFFSET_MS = 3 * HOUR_MS;

/** The last Sunday of `month` (0-based) at 01:00 UTC — when the EU changes its clocks. */
function lastSundayChangeover(year: number, month: number): number {
  const lastDay = Date.UTC(year, month + 1, 0);
  return lastDay - new Date(lastDay).getUTCDay() * DAY_MS + HOUR_MS;
}

const summerTimeByYear = new Map<number, readonly [number, number]>();

/** [start, end) of summer time in `year`, as instants. */
function summerTime(year: number): readonly [number, number] {
  let span = summerTimeByYear.get(year);
  if (!span) {
    span = [lastSundayChangeover(year, 2), lastSundayChangeover(year, 9)];
    summerTimeByYear.set(year, span);
  }
  return span;
}

/** Romania's offset from UTC at an instant, in minutes: 180 in summer time, else 120. */
export function romaniaOffsetMinutes(instantMs: number): number {
  return romaniaOffsetMs(instantMs) / MINUTE_MS;
}

function romaniaOffsetMs(instantMs: number): number {
  const [start, end] = summerTime(new Date(instantMs).getUTCFullYear());
  return instantMs >= start && instantMs < end ? SUMMER_OFFSET_MS : WINTER_OFFSET_MS;
}

/** `YYYY-MM-DD` of the UTC day an epoch-ms value falls in — the calendar-date form. */
function dateKeyOfUtc(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** UTC midnight of a `YYYY-MM-DD` date, as epoch ms; NaN when it is not one. */
function utcMidnightOf(date: string): number {
  return Date.parse(`${date}T00:00:00.000Z`);
}

/** The calendar date it is in Romania at an instant, `YYYY-MM-DD`. */
export function romaniaDateKey(instantMs: number): string {
  return dateKeyOfUtc(instantMs + romaniaOffsetMs(instantMs));
}

/** The Romanian date and time of day of an instant: minutes from 00:00, 0–1439. */
export function romaniaClock(instantMs: number): { date: string; minutes: number } {
  const wall = instantMs + romaniaOffsetMs(instantMs);
  const midnight = Math.floor(wall / DAY_MS) * DAY_MS;
  return { date: dateKeyOfUtc(midnight), minutes: Math.floor((wall - midnight) / MINUTE_MS) };
}

/**
 * The instant it is `minutes` past 00:00 on `date` in Romania (1440 is the next
 * day's midnight). NaN when `date` is not a `YYYY-MM-DD` date.
 */
export function romaniaInstant(date: string, minutes = 0): number {
  const wall = utcMidnightOf(date) + minutes * MINUTE_MS;
  if (!Number.isFinite(wall)) return Number.NaN;
  // The offset in force were it winter time, confirmed at the instant that
  // gives: the two readings differ only around a changeover, where the second
  // lands a missing time after the jump and a doubled one on its second pass.
  const guess = wall - romaniaOffsetMs(wall - WINTER_OFFSET_MS);
  return wall - romaniaOffsetMs(guess);
}

/** The instant the Romanian day an instant falls in began (00:00 in Romania). */
export function romaniaDayStart(instantMs: number): number {
  return romaniaInstant(romaniaDateKey(instantMs), 0);
}

/** `date` moved by whole calendar days, `YYYY-MM-DD`. */
export function addCalendarDays(date: string, days: number): string {
  return dateKeyOfUtc(utcMidnightOf(date) + days * DAY_MS);
}

/** The weekday of a calendar date: 0 = Sunday … 6 = Saturday. */
export function calendarWeekday(date: string): number {
  return new Date(utcMidnightOf(date)).getUTCDay();
}

/** The legal days off in Romania (Codul muncii, art. 139), by what they mark. */
export const romanianPublicHolidayKinds = [
  'NEW_YEAR',
  'NEW_YEAR_SECOND_DAY',
  'EPIPHANY',
  'SAINT_JOHN',
  'UNION_DAY',
  'GOOD_FRIDAY',
  'EASTER',
  'EASTER_MONDAY',
  'LABOUR_DAY',
  'CHILDREN_DAY',
  'PENTECOST',
  'PENTECOST_MONDAY',
  'ASSUMPTION',
  'SAINT_ANDREW',
  'NATIONAL_DAY',
  'CHRISTMAS',
  'CHRISTMAS_SECOND_DAY',
] as const;
export type RomanianPublicHolidayKind = (typeof romanianPublicHolidayKinds)[number];

export interface RomanianPublicHoliday {
  date: string;
  kind: RomanianPublicHolidayKind;
}

/**
 * Orthodox Easter Sunday of `year`, `YYYY-MM-DD` (Gregorian): Meeus' Julian
 * computus, moved 13 days onto the Gregorian calendar — right for 1900–2099.
 */
export function orthodoxEaster(year: number): string {
  const a = year % 4;
  const b = year % 7;
  const c = year % 19;
  const d = (19 * c + 15) % 30;
  const e = (2 * a + 4 * b - d + 34) % 7;
  const month = Math.floor((d + e + 114) / 31);
  const day = ((d + e + 114) % 31) + 1;
  return dateKeyOfUtc(Date.UTC(year, month - 1, day + 13));
}

const holidaysByYear = new Map<number, readonly RomanianPublicHoliday[]>();

/**
 * The legal holidays of `year`, by date. The list in force since 2024: New
 * Year (two days), Epiphany and Saint John (6–7 January), the Union Day,
 * Orthodox Good Friday, Easter and Pentecost (Sunday and Monday each), 1 May,
 * 1 June, the Assumption, Saint Andrew, the National Day and Christmas (two
 * days). Two can fall on one date (Pentecost Monday on 1 June in 2026).
 */
export function romanianPublicHolidays(year: number): readonly RomanianPublicHoliday[] {
  let list = holidaysByYear.get(year);
  if (list) return list;
  const fixed = (month: number, day: number): string =>
    dateKeyOfUtc(Date.UTC(year, month - 1, day));
  const easter = orthodoxEaster(year);
  const holidays: RomanianPublicHoliday[] = [
    { date: fixed(1, 1), kind: 'NEW_YEAR' },
    { date: fixed(1, 2), kind: 'NEW_YEAR_SECOND_DAY' },
    { date: fixed(1, 6), kind: 'EPIPHANY' },
    { date: fixed(1, 7), kind: 'SAINT_JOHN' },
    { date: fixed(1, 24), kind: 'UNION_DAY' },
    { date: addCalendarDays(easter, -2), kind: 'GOOD_FRIDAY' },
    { date: easter, kind: 'EASTER' },
    { date: addCalendarDays(easter, 1), kind: 'EASTER_MONDAY' },
    { date: fixed(5, 1), kind: 'LABOUR_DAY' },
    { date: fixed(6, 1), kind: 'CHILDREN_DAY' },
    { date: addCalendarDays(easter, 49), kind: 'PENTECOST' },
    { date: addCalendarDays(easter, 50), kind: 'PENTECOST_MONDAY' },
    { date: fixed(8, 15), kind: 'ASSUMPTION' },
    { date: fixed(11, 30), kind: 'SAINT_ANDREW' },
    { date: fixed(12, 1), kind: 'NATIONAL_DAY' },
    { date: fixed(12, 25), kind: 'CHRISTMAS' },
    { date: fixed(12, 26), kind: 'CHRISTMAS_SECOND_DAY' },
  ];
  list = Object.freeze(
    holidays.sort(
      (left, right) =>
        left.date.localeCompare(right.date) ||
        romanianPublicHolidayKinds.indexOf(left.kind) -
          romanianPublicHolidayKinds.indexOf(right.kind),
    ),
  );
  holidaysByYear.set(year, list);
  return list;
}

/** The legal holidays on a date — usually none or one; [] for a date that is not one. */
export function romanianPublicHolidaysOn(date: string): readonly RomanianPublicHolidayKind[] {
  const year = Number(date.slice(0, 4));
  if (!Number.isInteger(year)) return [];
  return romanianPublicHolidays(year)
    .filter((holiday) => holiday.date === date)
    .map((holiday) => holiday.kind);
}

/** Whether a date is a legal holiday in Romania. */
export function isRomanianPublicHoliday(date: string): boolean {
  return romanianPublicHolidaysOn(date).length > 0;
}
